import React, { useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import {
  SalvButton,
  SalvDropdown,
  SalvModal,
  SalvRadio,
  SalvRadioGroup,
  SalvTextField,
  Size,
} from '@salvapp/ui-components';
import { scenarioInternalApi } from '../apis';
import { convertToDropdownOption } from '../common/helper';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import {
  InternalApiScenarioAlertedPersonCategory,
  InternalApiScenarioEntityType,
  InternalApiScenarioTypeV2,
} from '../api-internal';

import './NewScenarioModal.scss';
import * as Yup from 'yup';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import { Link } from 'react-router-dom';
import { useMe } from '../organisation/useMe';
import { useQueryScenarioCategories, useQueryScenarioTypologies } from './scenarioQueryActions';

export interface NewScenarioFormValues {
  internalId: string;
  name: string;
  category: DropdownOption | undefined;
  typology: DropdownOption | undefined;
  type: InternalApiScenarioTypeV2 | undefined;
  entity: InternalApiScenarioEntityType | undefined;
  personCategory: InternalApiScenarioAlertedPersonCategory | undefined;
}

interface NewScenarioModalProps {
  showModal: boolean;
  onClose: () => void;
  onCreate: (scenarioId: string) => void;
}

const NewScenarioModal = ({ showModal, onClose, onCreate }: NewScenarioModalProps) => {
  const [selectedCategory, setSelectedCategory] = useState<DropdownOption>();
  const { features } = useMe();

  const { data: categories } = useQueryScenarioCategories();
  const { data: typologies } = useQueryScenarioTypologies(selectedCategory?.value);

  const isCounterpartyEnabled = isFeatureEnabled(ORGANISATION_FEATURE.COUNTERPARTY, features);
  const isPeriodicEntityTypeEnabled = isFeatureEnabled(ORGANISATION_FEATURE.SCENARIO_PERIODIC_ENTITY_TYPE, features);

  const initialValues: NewScenarioFormValues = {
    internalId: '',
    name: '',
    category: undefined,
    typology: undefined,
    type: undefined,
    entity: undefined,
    personCategory: undefined,
  };

  const validationSchema = Yup.object().shape({
    internalId: Yup.string().required('Internal ID is required'),
    name: Yup.string().required('Name is required'),
    category: Yup.object().required('Category is required'),
    typology: Yup.object().when([], {
      is: () => !!typologies.length,
      then: (schema) => schema.required('Typology is required'),
      otherwise: (schema) => schema,
    }),
    type: Yup.string().required('Type is required'),
    entity: Yup.string().required('Entity is required'),
    personCategory: Yup.string().required('Person category is required'),
  });

  const submit = async (
    values: NewScenarioFormValues,
    { setSubmitting, resetForm }: FormikHelpers<NewScenarioFormValues>
  ) => {
    try {
      const newScenario = await scenarioInternalApi.createScenario({
        internalApiCreateScenario: {
          internalId: values.internalId,
          name: values.name,
          categoryId: values.category!.value,
          typologyId: values.typology?.value,
          type: values.type!,
          entityType: values.entity!,
          alertedPersonCategory: values.personCategory!,
        },
      });

      onCreate(newScenario.id!);
      onClose();
      resetForm();
    } catch (_) {
      // do nothing, error pops up
    } finally {
      setSubmitting(false);
    }
  };

  const getDefaultTypology = () =>
    typologies.length === 1 ? convertToDropdownOption(typologies[0].name, typologies[0].id) : undefined;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, formikHelpers) => {
        submit(values, formikHelpers);
      }}
      validationSchema={validationSchema}
      validateOnChange={true}
      validateOnBlur={false}
    >
      {({ values, setFieldValue, isSubmitting, errors, touched, resetForm }: FormikProps<NewScenarioFormValues>) => {
        useEffect(() => {
          setFieldValue('typology', getDefaultTypology());
        }, [typologies]);

        return (
          <SalvModal
            show={showModal}
            dialogClassName='new-scenario-modal'
            onHide={() => {
              onClose();
              resetForm();
            }}
          >
            <Form>
              <SalvModal.Header>New custom scenario</SalvModal.Header>

              <SalvModal.Body>
                <SalvTextField
                  label='Internal ID'
                  required={true}
                  isInvalid={touched.internalId && !!errors.internalId}
                  onChange={(value: string) => setFieldValue('internalId', value.toUpperCase())}
                  value={values.internalId}
                />

                <SalvTextField
                  label='Scenario name'
                  required={true}
                  isInvalid={touched.name && !!errors.name}
                  onChange={(value: string) => setFieldValue('name', value)}
                  value={values.name}
                  disabled={isSubmitting}
                />

                <SalvDropdown
                  label='Category'
                  required={true}
                  isInvalid={touched.category && !!errors.category}
                  placeholder='Select category'
                  options={categories.map((category) => convertToDropdownOption(category.name, category.id))}
                  size={Size.MEDIUM}
                  onChange={([option]) => {
                    setFieldValue('category', option);
                    setSelectedCategory(option);
                  }}
                  value={values.category}
                  disabled={isSubmitting}
                  clearable={false}
                  renderedInBootstrapModal
                />

                <SalvDropdown
                  label='Typology'
                  required={true}
                  isInvalid={touched.typology && !!errors.typology}
                  placeholder='Select typology'
                  options={typologies.map((typology) => convertToDropdownOption(typology.name, typology.id))}
                  size={Size.MEDIUM}
                  onChange={(options) => {
                    if (options[0]) {
                      setFieldValue('typology', options[0]);
                    }
                  }}
                  value={values.typology}
                  disabled={isSubmitting || !typologies.length}
                  renderedInBootstrapModal
                />

                <div className='border-bottom'></div>

                <SalvRadioGroup
                  label='Scenario type'
                  description={
                    <div>
                      <div>
                        For more information on scenario types,
                        <Link to='https://help.salv.com/en/articles/149651-scenario-types' target='_blank'>
                          read here.
                        </Link>
                      </div>
                      <div>Scenario type cannot be changed after the scenario is created.</div>
                    </div>
                  }
                  required={true}
                  size={Size.MEDIUM}
                  onChange={(type) => {
                    setFieldValue('type', type as InternalApiScenarioTypeV2).then(() => {
                      if (type === InternalApiScenarioTypeV2.PERIODIC && isCounterpartyEnabled) {
                        setFieldValue('personCategory', '');
                      } else {
                        setFieldValue('personCategory', InternalApiScenarioAlertedPersonCategory.CUSTOMER);
                      }
                    });
                    if (type === InternalApiScenarioTypeV2.PERIODIC_ENTITY) {
                      setFieldValue('entity', InternalApiScenarioEntityType.PERSON);
                    }
                  }}
                  value={values.type}
                  disabled={isSubmitting}
                >
                  <div className='d-flex'>
                    <SalvRadio
                      value={InternalApiScenarioTypeV2.REAL_TIME}
                      label='Real-time'
                      invalid={touched.type && !!errors.type}
                    />
                    <SalvRadio
                      value={InternalApiScenarioTypeV2.POST_EVENT}
                      label='Post-event'
                      invalid={touched.type && !!errors.type}
                    />
                    <SalvRadio
                      value={InternalApiScenarioTypeV2.PERIODIC}
                      label='Periodic'
                      invalid={touched.type && !!errors.type}
                    />
                    {isPeriodicEntityTypeEnabled && (
                      <SalvRadio
                        value={InternalApiScenarioTypeV2.PERIODIC_ENTITY}
                        label='Periodic Entity'
                        invalid={touched.type && !!errors.type}
                      />
                    )}
                  </div>
                </SalvRadioGroup>

                <SalvRadioGroup
                  label='Alerting entity'
                  required={true}
                  size={Size.MEDIUM}
                  onChange={(entity) => {
                    setFieldValue('entity', entity as InternalApiScenarioEntityType);
                  }}
                  value={values.entity}
                  disabled={isSubmitting}
                >
                  <div className='d-flex'>
                    {values.type !== InternalApiScenarioTypeV2.PERIODIC_ENTITY &&
                      values.type !== InternalApiScenarioTypeV2.PERIODIC && (
                        <SalvRadio
                          value={InternalApiScenarioEntityType.TRANSACTION}
                          label='Transaction'
                          invalid={touched.entity && !!errors.entity}
                        />
                      )}
                    <SalvRadio
                      value={InternalApiScenarioEntityType.PERSON}
                      label='Person'
                      invalid={touched.entity && !!errors.entity}
                    />
                  </div>
                </SalvRadioGroup>

                {values.type === InternalApiScenarioTypeV2.PERIODIC && isCounterpartyEnabled && (
                  <SalvRadioGroup
                    label='Person Type'
                    required={true}
                    size={Size.MEDIUM}
                    onChange={(personCategory) => {
                      setFieldValue('personCategory', personCategory as InternalApiScenarioAlertedPersonCategory);
                    }}
                    value={values.personCategory}
                    disabled={isSubmitting}
                    defaultValue={values.personCategory}
                  >
                    <div className='d-flex'>
                      <SalvRadio
                        value={InternalApiScenarioAlertedPersonCategory.CUSTOMER}
                        label='Customer'
                        invalid={touched.personCategory && !!errors.personCategory}
                      />
                      <SalvRadio
                        value={InternalApiScenarioAlertedPersonCategory.COUNTERPARTY}
                        label='Counterparty'
                        disabled={values.type !== InternalApiScenarioTypeV2.PERIODIC}
                        invalid={touched.personCategory && !!errors.personCategory}
                      />
                    </div>
                  </SalvRadioGroup>
                )}
              </SalvModal.Body>

              <SalvModal.Footer>
                <SalvButton
                  variant='muted'
                  disabled={false}
                  size={Size.LARGE}
                  onClick={() => {
                    onClose();
                    resetForm();
                  }}
                  label='Cancel'
                />

                <SalvButton
                  type='submit'
                  variant='primary'
                  disabled={isSubmitting}
                  size={Size.LARGE}
                  label='Create Scenario'
                />
              </SalvModal.Footer>
            </Form>
          </SalvModal>
        );
      }}
    </Formik>
  );
};

export default NewScenarioModal;
